const TOKEN_KEY = "plh_token";

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem("plh_auth_disabled");
}

export function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

export function isAuthenticated(): boolean {
  const token = getToken();
  if (token !== null && !isTokenExpired(token)) return true;
  return sessionStorage.getItem("plh_auth_disabled") === "true";
}

/**
 * Validate the current session against the backend.
 * Returns whether the user is authenticated and whether auth is enabled server-side.
 * On network error, falls back to the local JWT expiry check.
 */
export async function validateSession(): Promise<{ authenticated: boolean; authEnabled: boolean }> {
  let domain = import.meta.env.VITE_PLH_API_URL || "";
  if (domain.endsWith("/")) domain = domain.slice(0, -1);

  const token = getToken();
  const headers: HeadersInit = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  try {
    const res = await fetch(`${domain}/auth/me`, { headers });
    if (res.ok) {
      return await res.json();
    }
    clearToken();
    return { authenticated: false, authEnabled: true };
  } catch {
    return { authenticated: isAuthenticated(), authEnabled: true };
  }
}

/**
 * Wrapper around fetch() that injects the Authorization header.
 * On 401, clears the stored token and reloads the page to show the login screen.
 */
export async function authFetch(
  url: string,
  options?: RequestInit
): Promise<Response> {
  const token = getToken();
  const headers = new Headers(options?.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    clearToken();
    window.location.reload();
  }

  return response;
}
